/*
 * viewer.js — page rendering and the interactive object layer for /pdf-editor.
 *
 * RENDERING
 * ─────────
 * Pages are built as empty boxes immediately and rasterised only when they
 * approach the viewport; off-screen canvases are released once the document is
 * large. That pattern is lifted from /sign-pdf, where it is what keeps a
 * 150-page file from exhausting mobile memory. A PDF viewer that renders every
 * page up front looks fine on a 3-page invoice and dies on a scanned contract.
 *
 * THE OVERLAY IS DOM, NOT CANVAS
 * ──────────────────────────────
 * Objects live as absolutely-positioned elements over the page canvas rather
 * than being painted into it. That buys three things a canvas overlay cannot:
 * text stays crisp at any zoom because it is text; editing a text box uses a
 * real contenteditable with a real caret, IME and spellcheck; and screen
 * readers and keyboard focus work without being re-implemented.
 *
 * Geometry is stored ONLY as page fractions in the session. This module
 * converts to and from pixels at the moment of interaction and never caches a
 * pixel value — that is what makes a placement survive zoom, window resize and
 * devicePixelRatio changes instead of drifting.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfEditorViewer = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    var DPR = Math.min((typeof window !== 'undefined' && window.devicePixelRatio) || 1, 2);
    var RECYCLE_ABOVE = 20;          // pages, before off-screen canvases are freed

    function el(tag, cls, parent) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (parent) parent.appendChild(n);
        return n;
    }

    /*
     * create({ container, session, pdfDoc, onSelect, onChange, t })
     */
    function create(cfg) {
        var container = cfg.container;
        var S = cfg.S;                       // PdfSession module
        var G = cfg.G;                       // PdfEditorGeom module
        var st = cfg.session;
        var pdfDoc = cfg.pdfDoc;

        var zoom = 1;                        // multiplier over fit-to-width
        var pageEls = {};                    // pageId -> { wrap, canvas, overlay, ... }
        var selectedId = null;
        var mode = 'select';
        var renderTasks = {};

        /* ── geometry ──────────────────────────────────────────────────── */

        /* The scale a page gets at zoom 1 — i.e. "fit to width". Split out of
           fitScale so the zoom presets can ask what 100% actually means for
           THIS page instead of assuming a number. */
        function baseScale(page) {
            var box = S.displayBox(page);
            var avail = Math.max(240, container.clientWidth - 28);
            return Math.min(avail / box.width, 2.5);
        }

        function fitScale(page) {
            return baseScale(page) * zoom;
        }

        /* Zoom at which the page currently in view fits the stage vertically.
           Derived from the real box, never a guessed constant: an A4 and a
           landscape slide need different numbers for the same stage. */
        function fitPageZoom() {
            var id = currentPageId();
            var page = id && S.visiblePages(st).filter(function (p) { return p.id === id; })[0];
            if (!page) page = S.visiblePages(st)[0];
            if (!page) return 1;
            var box = S.displayBox(page);
            var availH = Math.max(200, container.clientHeight - 36);
            var z = availH / (box.height * baseScale(page));
            return Math.max(0.25, Math.min(4, z));
        }

        /* 1-based position of the page in view, and the jump to one. Both work
           off the same visiblePages order the thumbnails use, so the number in
           the status bar and the highlighted thumbnail can never disagree. */
        function pageIndex() {
            var id = currentPageId();
            var list = S.visiblePages(st);
            for (var i = 0; i < list.length; i++) if (list[i].id === id) return i + 1;
            return list.length ? 1 : 0;
        }

        function goToPage(n) {
            var list = S.visiblePages(st);
            var page = list[Math.max(0, Math.min(list.length - 1, (n | 0) - 1))];
            var rec = page && pageEls[page.id];
            if (!rec) return;
            // Rect delta, NOT offsetTop: offsetTop is measured from the nearest
            // POSITIONED ancestor, and the stage is not one - so offsetTop came
            // back page-relative and "next page" scrolled to 1994px, two pages
            // past the target. The delta between the two live rects cannot be
            // wrong about which box it is measuring.
            //
            // This moves the stage's own scrollTop, never the page under the
            // user (project rule: no page-level scrolling on a user action).
            container.scrollTop += rec.wrap.getBoundingClientRect().top
                                 - container.getBoundingClientRect().top - 14;
        }

        /* ── page shells ───────────────────────────────────────────────── */

        function buildPages() {
            container.innerHTML = '';
            pageEls = {};
            var visible = S.visiblePages(st);

            visible.forEach(function (page, i) {
                var box = S.displayBox(page);
                var scale = fitScale(page);
                var wrap = el('div', 'pe-page', container);
                wrap.dataset.pageId = page.id;
                wrap.style.width = Math.round(box.width * scale) + 'px';
                wrap.style.height = Math.round(box.height * scale) + 'px';

                var canvas = el('canvas', 'pe-page-canvas', wrap);
                var overlay = el('div', 'pe-overlay', wrap);
                overlay.dataset.pageId = page.id;

                var label = el('div', 'pe-page-label', wrap);
                label.textContent = (i + 1) + ' / ' + visible.length;

                pageEls[page.id] = {
                    page: page, wrap: wrap, canvas: canvas, overlay: overlay,
                    scale: scale, rendered: false,
                };
                observer.observe(wrap);
            });
            drawAllOverlays();
        }

        var observer = (typeof IntersectionObserver !== 'undefined')
            ? new IntersectionObserver(function (entries) {
                entries.forEach(function (en) {
                    var rec = pageEls[en.target.dataset.pageId];
                    if (!rec) return;
                    if (en.isIntersecting) renderPage(rec);
                    else if (S.pageCount(st) > RECYCLE_ABOVE) recyclePage(rec);
                });
            }, { root: null, rootMargin: '800px 0px' })
            : { observe: function () {}, unobserve: function () {}, disconnect: function () {} };

        function renderPage(rec) {
            if (rec.rendered || rec.page.src === null) {
                if (rec.page.src === null) paintBlank(rec);
                return;
            }
            if (renderTasks[rec.page.id]) return;
            var task = pdfDoc.getPage(rec.page.src + 1).then(function (pdfPage) {
                // pdf.js applies the file's /Rotate itself; the user's extra
                // turn is added here so what is drawn matches displayBox().
                var rotation = (pdfPage.rotate || 0) + rec.page.turn;
                var vp = pdfPage.getViewport({ scale: rec.scale, rotation: ((rotation % 360) + 360) % 360 });
                rec.canvas.width = Math.round(vp.width * DPR);
                rec.canvas.height = Math.round(vp.height * DPR);
                rec.canvas.style.width = Math.round(vp.width) + 'px';
                rec.canvas.style.height = Math.round(vp.height) + 'px';
                var ctx = rec.canvas.getContext('2d', { alpha: false });
                ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, vp.width, vp.height);
                return pdfPage.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
                    rec.rendered = true;
                    rec.wrap.classList.add('is-rendered');
                    // The drawn page, turned as the person sees it — the
                    // sideways check reads its pixels (orient.js).
                    if (cfg.onPageRendered) { try { cfg.onPageRendered(rec.page, rec.canvas); } catch (e) {} }
                });
            }).catch(function () {
                // A page that will not rasterise must not take the document
                // down with it — the rest stays editable and the failure is
                // visible on that page alone.
                rec.wrap.classList.add('is-failed');
            }).then(function () { delete renderTasks[rec.page.id]; });
            renderTasks[rec.page.id] = task;
        }

        function paintBlank(rec) {
            var box = S.displayBox(rec.page);
            rec.canvas.width = Math.round(box.width * rec.scale * DPR);
            rec.canvas.height = Math.round(box.height * rec.scale * DPR);
            rec.canvas.style.width = Math.round(box.width * rec.scale) + 'px';
            rec.canvas.style.height = Math.round(box.height * rec.scale) + 'px';
            var ctx = rec.canvas.getContext('2d');
            ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, box.width * rec.scale, box.height * rec.scale);
            rec.rendered = true;
        }

        function recyclePage(rec) {
            if (!rec.rendered) return;
            rec.canvas.width = rec.canvas.height = 0;
            rec.rendered = false;
            rec.wrap.classList.remove('is-rendered');
        }

        /* ── object layer ──────────────────────────────────────────────── */

        function drawAllOverlays() {
            Object.keys(pageEls).forEach(function (id) { drawOverlay(id); });
        }

        function drawOverlay(pageId) {
            var rec = pageEls[pageId];
            if (!rec) return;
            rec.overlay.innerHTML = '';
            S.objectsOn(st, pageId).forEach(function (o) {
                rec.overlay.appendChild(buildObjectEl(o, rec));
            });
            if (selectedId) markSelected(selectedId);
        }

        function buildObjectEl(o, rec) {
            var box = S.displayBox(rec.page);
            var w = box.width * rec.scale, h = box.height * rec.scale;
            var node = el('div', 'pe-obj pe-obj-' + o.type);
            node.dataset.objId = o.id;
            node.style.left = (o.placement.nx * w) + 'px';
            node.style.top = (o.placement.ny * h) + 'px';
            node.style.width = (o.placement.nw * w) + 'px';
            node.style.height = (o.placement.nh * h) + 'px';
            if (o.placement.rot) node.style.transform = 'rotate(' + o.placement.rot + 'deg)';
            if (o.opacity !== undefined) node.style.opacity = o.opacity;

            switch (o.type) {
                case 'text': {
                    // An OCR line is invisible in the OUTPUT by design, but it
                    // must be findable in the EDITOR — otherwise the user has
                    // no way to see what was recognised or to correct it.
                    if (o.ocr) node.classList.add('is-ocr');
                    var tx = el('div', 'pe-obj-text', node);
                    tx.textContent = o.text || '';
                    tx.style.fontSize = (o.size || 14) * rec.scale + 'px';
                    tx.style.color = o.color || '#111111';
                    tx.style.textAlign = o.align || 'left';
                    tx.style.lineHeight = (o.lineHeight || 1.25);
                    tx.style.fontFamily = cssFamilyFor(o.faceId, o.bold);
                    tx.style.fontWeight = o.bold ? '700' : '400';
                    break;
                }
                case 'image':
                case 'signature': {
                    var img = el('img', 'pe-obj-img', node);
                    img.src = o.dataUrl || '';
                    img.alt = '';
                    img.draggable = false;
                    break;
                }
                case 'formfield': {
                    node.style.border = '1.5px dashed rgba(37,99,235,.8)';
                    node.style.background = 'rgba(219,234,254,.35)';
                    var lbl = el('span', 'pe-obj-tag', node);
                    var mark = { checkbox: '☑ ', dropdown: '▾ ', radio: '◉ ', textarea: '≡ ' }[o.fieldKind] || 'T ';
                    // A radio button's own option is what distinguishes it from
                    // its siblings, so it is shown next to the shared name.
                    var extra = (o.fieldKind === 'radio' && o.fieldOptions && o.fieldOptions[0])
                        ? ' · ' + o.fieldOptions[0] : '';
                    lbl.textContent = mark + (o.fieldName || '') + extra;
                    break;
                }
                case 'link': {
                    node.style.border = '1.5px solid rgba(22,163,74,.85)';
                    node.style.background = 'rgba(220,252,231,.30)';
                    var lnk = el('span', 'pe-obj-tag', node);
                    lnk.textContent = '🔗 ' + String(o.url || '').replace(/^https?:\/\//, '').slice(0, 28);
                    break;
                }
                case 'redact': {
                    // Opaque on screen because it is opaque in the output — the
                    // preview must not suggest the text is merely dimmed.
                    node.style.background = o.fill || '#000000';
                    break;
                }
                case 'rect':
                case 'whiteout': {
                    node.style.background = (o.type === 'whiteout') ? (o.fill || '#ffffff') : (o.fill || 'transparent');
                    if (o.stroke) node.style.border = (o.strokeWidth || 1) + 'px solid ' + o.stroke;
                    break;
                }
                case 'ellipse': {
                    node.style.background = o.fill || 'transparent';
                    node.style.borderRadius = '50%';
                    if (o.stroke) node.style.border = (o.strokeWidth || 1) + 'px solid ' + o.stroke;
                    break;
                }
                case 'highlight': {
                    node.style.background = o.color || '#ffe600';
                    node.style.mixBlendMode = 'multiply';
                    break;
                }
                case 'ink':
                case 'line': {
                    // Freehand and straight lines are both point arrays, and
                    // export draws them through the same path writer — so the
                    // preview has to use one renderer too, or the two drift.
                    // Renders as SVG so it stays vector-sharp at any zoom.
                    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svg.setAttribute('class', 'pe-obj-ink');
                    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
                    svg.setAttribute('preserveAspectRatio', 'none');
                    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                    path.setAttribute('d', inkPath(o.points, w, h));
                    path.setAttribute('fill', 'none');
                    path.setAttribute('stroke', o.color || '#e11d48');
                    path.setAttribute('stroke-width', (o.strokeWidth || 2) * rec.scale);
                    path.setAttribute('stroke-linecap', 'round');
                    path.setAttribute('stroke-linejoin', 'round');
                    svg.appendChild(path);
                    // ink spans the page, not a box
                    node.style.left = '0'; node.style.top = '0';
                    node.style.width = w + 'px'; node.style.height = h + 'px';
                    node.appendChild(svg);
                    break;
                }
            }

            if (o.type !== 'ink' && o.type !== 'line') {
                ['nw', 'ne', 'sw', 'se'].forEach(function (corner) {
                    var g = el('span', 'pe-handle pe-handle-' + corner, node);
                    g.dataset.resize = corner;
                });
                var rot = el('span', 'pe-handle pe-handle-rot', node);
                rot.dataset.rotate = '1';
            }
            return node;
        }

        function inkPath(points, w, h) {
            if (!points || !points.length) return '';
            var d = '';
            points.forEach(function (p, i) {
                d += (i ? 'L' : 'M') + (p[0] * w).toFixed(2) + ' ' + (p[1] * h).toFixed(2) + ' ';
            });
            return d.trim();
        }

        /* The preview is drawn with the SAME TTF the exporter embeds, and its
           fallback is the exporter's own ladder (PdfTextFont.cssFamily /
           cssStack, the door the card tools use). Until 2026-10-22 this asked
           for families named "PE Noto Sans", "PE Playfair Display"… that
           nothing ever registered, so every preview was a system font —
           Segoe UI on Windows — while the file got Noto Sans: another width,
           other line breaks, text that fitted a table cell on screen and ran
           past it on paper. */
        function cssFamilyFor(faceId, bold) {
            var TF = window.PdfTextFont;
            var mine = (TF && TF.cssFamily) ? TF.cssFamily(faceId || 'noto', !!bold) : null;
            var stack = (TF && TF.cssStack) ? TF.cssStack(!!bold) : '';
            return (mine ? '"' + mine + '", ' : '') + (stack ? stack + ', ' : '') + 'sans-serif';
        }

        /* ── selection ─────────────────────────────────────────────────── */

        function markSelected(id) {
            container.querySelectorAll('.pe-obj.is-selected').forEach(function (n) {
                n.classList.remove('is-selected');
            });
            if (!id) return;
            var node = container.querySelector('[data-obj-id="' + id + '"]');
            if (node) node.classList.add('is-selected');
        }

        function select(id) {
            selectedId = id;
            markSelected(id);
            if (cfg.onSelect) cfg.onSelect(id ? S.objectById(st, id) : null);
        }

        /* ── interaction ───────────────────────────────────────────────── */

        var drag = null;

        function pageRecFromEvent(e) {
            var overlay = e.target.closest && e.target.closest('.pe-overlay');
            if (!overlay) return null;
            return pageEls[overlay.dataset.pageId] || null;
        }

        function localFraction(rec, clientX, clientY) {
            var r = rec.overlay.getBoundingClientRect();
            return {
                nx: (clientX - r.left) / r.width,
                ny: (clientY - r.top) / r.height,
            };
        }

        function onPointerDown(e) {
            var rec = pageRecFromEvent(e);
            if (!rec) return;

            var handle = e.target.closest('.pe-handle');
            var objNode = e.target.closest('.pe-obj');

            // A drawing tool means the user wants to draw, full stop. The `&&
            // !objNode` this replaced meant a pointerdown landing on ANY
            // existing object silently turned the gesture into "drag that
            // object" instead: highlighting text under a whiteout box, or
            // drawing a second freehand stroke anywhere near the first, moved
            // the earlier object and drew nothing.
            if (mode !== 'select') {
                if (cfg.onCanvasDown) cfg.onCanvasDown(rec.page.id, localFraction(rec, e.clientX, e.clientY), e);
                return;
            }
            if (!objNode) { select(null); return; }

            var id = objNode.dataset.objId;
            select(id);
            var o = S.objectById(st, id);
            if (!o) return;

            // One history entry per gesture: commit now, then stream updates
            // with history:false so a drag is a single undo step.
            S.commit(st);
            drag = {
                id: id, rec: rec,
                start: localFraction(rec, e.clientX, e.clientY),
                origin: JSON.parse(JSON.stringify(o.placement)),
                kind: handle ? (handle.dataset.rotate ? 'rotate' : 'resize') : 'move',
                corner: handle && handle.dataset.resize,
            };
            // Capture is an optimisation — it keeps the drag alive if the
            // pointer leaves the element — not a requirement. It THROWS
            // (NotFoundError) when the pointer id is no longer active: a very
            // fast tap, a pointercancel race, or a synthetic event. Unguarded,
            // that exception escapes the handler and skips everything after it,
            // so the drag this function just set up never starts.
            try { objNode.setPointerCapture && objNode.setPointerCapture(e.pointerId); } catch (err) {}
            e.preventDefault();
        }

        function onPointerMove(e) {
            if (!drag) return;
            var now = localFraction(drag.rec, e.clientX, e.clientY);
            var dx = now.nx - drag.start.nx;
            var dy = now.ny - drag.start.ny;
            var p = drag.origin;
            var next, guides = [];

            if (drag.kind === 'move') {
                next = G.move(p, dx, dy, {});
                // Alt suppresses snapping — the escape hatch for the one case
                // where the user genuinely wants an off-grid position.
                if (!e.altKey) {
                    var neighbours = S.objectsOn(st, drag.rec.page.id)
                        .filter(function (o) { return o.id !== drag.id; })
                        .map(function (o) { return o.placement; });
                    var snapped = G.alignGuides(next, neighbours, 0.008 / Math.max(zoom, 0.5));
                    guides = snapped.guides;
                    next = snapped;
                }
            } else if (drag.kind === 'resize') {
                next = G.resize(p, drag.corner, dx, dy, {
                    keepAspect: e.shiftKey || drag.lockAspect,
                });
            } else {
                var ob = drag.rec.overlay.getBoundingClientRect();
                var turned = S.objectById(st, drag.id);
                next = G.rotateTo(p, now, {
                    aspect: ob.height ? ob.width / ob.height : 1,
                    snap: e.shiftKey,
                    free: e.altKey,
                    // A PDF turns a form field or a link only by right angles
                    // (/MK /R, an upright /Rect): offering 37° would show one
                    // thing and write another.
                    snapStep: (turned && (turned.type === 'formfield' || turned.type === 'link')) ? 90 : 0,
                });
            }
            S.updateObject(st, drag.id, { placement: next }, { history: false });
            drawOverlay(drag.rec.page.id);
            markSelected(drag.id);
            showGuides(drag.rec, guides);
        }

        /* Alignment guides are drawn only while a drag is snapped, and removed
           the moment it is not — a guide that lingers reads as page furniture. */
        function showGuides(rec, guides) {
            var layer = rec.wrap.querySelector('.pe-guides');
            if (!guides || !guides.length) { if (layer) layer.remove(); return; }
            if (!layer) { layer = el('div', 'pe-guides', rec.wrap); }
            layer.innerHTML = '';
            guides.forEach(function (g) {
                var line = el('i', 'pe-guide pe-guide-' + g.axis, layer);
                if (g.axis === 'x') line.style.left = (g.at * 100) + '%';
                else line.style.top = (g.at * 100) + '%';
            });
        }

        function onPointerUp() {
            if (!drag) return;
            drag = null;
            if (cfg.onChange) cfg.onChange();
        }

        container.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);

        /* ── public ────────────────────────────────────────────────────── */

        function setZoom(z) {
            zoom = Math.max(0.25, Math.min(4, z));
            buildPages();
        }

        function refresh() { buildPages(); }

        function refreshOverlays() { drawAllOverlays(); }

        /* The page the user is looking at — the one whose centre is nearest the
           viewport centre. Computed on demand so a newly added object lands on
           the visible page rather than always on page one. */
        function currentPageId() {
            // Measured against the STAGE, not the window: the pages scroll
            // inside a bounded container, so window centre can name a page that
            // is not on screen at all — and a new object would land on it.
            var cr = container.getBoundingClientRect();
            var mid = cr.top + cr.height / 2;
            var best = null, bestD = Infinity;
            Object.keys(pageEls).forEach(function (id) {
                var r = pageEls[id].wrap.getBoundingClientRect();
                var d = Math.abs((r.top + r.height / 2) - mid);
                if (d < bestD) { bestD = d; best = id; }
            });
            return best || (S.visiblePages(st)[0] || {}).id;
        }

        function destroy() {
            observer.disconnect();
            container.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
        }

        return {
            build: buildPages,
            refresh: refresh,
            refreshOverlays: refreshOverlays,
            drawOverlay: drawOverlay,
            select: select,
            selected: function () { return selectedId; },
            setMode: function (m) { mode = m; container.dataset.mode = m; },
            mode: function () { return mode; },
            setZoom: setZoom,
            zoom: function () { return zoom; },
            fitPageZoom: fitPageZoom,
            pageIndex: pageIndex,
            pageCount: function () { return S.visiblePages(st).length; },
            goToPage: goToPage,
            currentPageId: currentPageId,
            pageRec: function (id) { return pageEls[id]; },
            destroy: destroy,
        };
    }

    return { create: create };
});
